/**
 * Airmaxx CRM - Date Utilities (Единый модуль для работы с датами)
 * 
 * Это ОСНОВНОЙ файл для работы с датами. Используй его везде.
 * dateHelper.js - deprecated, использует re-export из этого файла.
 * 
 * ВАЖНО: Все даты в системе работают в timezone America/Los_Angeles (PST/PDT)
 * 
 * ПРАВИЛА РАБОТЫ С ДАТАМИ:
 * 
 * 1. DATE (только дата, без времени):
 *    - Используй getDateFromPicker() для извлечения даты из DatePicker
 *    - Используй formatDateOnly() для отображения
 *    - НЕ используй moment.utc() для дат из DatePicker!
 * 
 * 2. DATETIME (дата + время):
 *    - Используй parseDateTime() или toLocal() для парсинга
 *    - Используй formatDateTime() для отображения
 *    - Храни в базе в UTC, отображай в локальном timezone
 * 
 * 3. DatePicker события:
 *    - event.$d содержит Date в локальном времени
 *    - Используй getDateFromPicker() для извлечения даты
 * 
 * 4. Конвертация для API:
 *    - Используй toUTC() для конвертации в UTC ISO string
 *    - Используй toLocal() для получения moment в локальном timezone
 * 
 * ТИПИЧНЫЕ ОШИБКИ:
 * ❌ moment.utc(event.$d).format('YYYY-MM-DD') - сдвигает дату на timezone offset
 * ✅ moment(event.$d).format('YYYY-MM-DD') - сохраняет локальную дату
 * 
 * ❌ moment(isoString).format('MM/DD/YYYY') - может показать неправильный день
 * ✅ moment(isoString).tz('America/Los_Angeles').format('MM/DD/YYYY') - правильно
 * 
 * ❌ import moment from 'moment' - без timezone support
 * ✅ import { moment } from '../utils/dateUtils' - с настроенным timezone
 */

import moment from 'moment-timezone';

// ============================================
// CONSTANTS
// ============================================

/** Системный timezone (Pacific Time) */
export const SYSTEM_TIMEZONE = 'America/Los_Angeles';

/** Default timezone для обратной совместимости с dateHelper */
export const DEFAULT_TIMEZONE = SYSTEM_TIMEZONE;

// Текущий timezone пользователя (может быть изменён через setUserTimezone)
let userTimezone = SYSTEM_TIMEZONE;

// Устанавливаем default timezone для moment
moment.tz.setDefault(SYSTEM_TIMEZONE);

// ============================================
// TIMEZONE MANAGEMENT
// ============================================

/**
 * Установить timezone пользователя
 * Используется при логине для установки timezone из профиля пользователя
 * 
 * @param {string} timezone - IANA timezone string (e.g. 'America/Los_Angeles')
 */
export const setUserTimezone = (timezone) => {
  if (timezone && moment.tz.zone(timezone)) {
    userTimezone = timezone;
    moment.tz.setDefault(timezone);
  } else {
    userTimezone = SYSTEM_TIMEZONE;
    moment.tz.setDefault(SYSTEM_TIMEZONE);
  }
};

/**
 * Получить текущий timezone пользователя
 * @returns {string} IANA timezone string
 */
export const getUserTimezone = () => userTimezone;

/**
 * Получить default timezone системы
 * @returns {string} IANA timezone string
 */
export const getDefaultTimezone = () => DEFAULT_TIMEZONE;

/**
 * Извлечь дату из события DatePicker (MUI)
 * Используй эту функцию вместо moment.utc(event.$d)
 * 
 * @param {Object} event - Событие от DatePicker (содержит $d)
 * @returns {string} Дата в формате YYYY-MM-DD
 * 
 * @example
 * const handleDateChange = (event) => {
 *   const date = getDateFromPicker(event);
 *   // date = "2025-01-06"
 * };
 */
export const getDateFromPicker = (event) => {
  if (!event || !event.$d) return null;
  // НЕ используем UTC - сохраняем локальную дату
  return moment(event.$d).format('YYYY-MM-DD');
};

/**
 * Создать ISO datetime строку для конкретной даты и времени
 * 
 * @param {string} date - Дата в формате YYYY-MM-DD
 * @param {string} time - Время в формате HH:mm (24h) или hh:mm A (12h)
 * @returns {string} ISO datetime строка в UTC
 * 
 * @example
 * createDateTime('2025-01-06', '14:00') // "2025-01-06T22:00:00.000Z"
 */
export const createDateTime = (date, time) => {
  if (!date) return null;
  
  // Парсим время
  let hours, minutes;
  if (time) {
    const timeMoment = moment(time, ['HH:mm', 'hh:mm A']);
    hours = timeMoment.hours();
    minutes = timeMoment.minutes();
  } else {
    hours = 12;
    minutes = 0;
  }
  
  // Создаём datetime в локальном timezone и конвертируем в UTC
  const datetime = moment.tz(date, SYSTEM_TIMEZONE)
    .set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });
  
  return datetime.utc().format();
};

/**
 * Создать ISO date строку (полночь в UTC для данной локальной даты)
 * Используй для полей типа preferredDate где важна только дата
 * 
 * @param {string} date - Дата в формате YYYY-MM-DD
 * @returns {string} ISO date строка
 * 
 * @example
 * createDateOnly('2025-01-06') // "2025-01-06T08:00:00.000Z" (полночь PST = 8:00 UTC)
 */
export const createDateOnly = (date) => {
  if (!date) return null;
  // Полночь в локальном времени, конвертируем в UTC
  return moment.tz(date, SYSTEM_TIMEZONE).startOf('day').utc().format();
};

/**
 * Парсить ISO datetime и получить moment объект в локальном timezone
 * 
 * @param {string} isoString - ISO datetime строка
 * @returns {moment.Moment} Moment объект в SYSTEM_TIMEZONE
 * 
 * @example
 * const dt = parseDateTime('2025-01-06T22:00:00.000Z');
 * dt.format('MM/DD/YYYY h:mm A') // "01/06/2025 2:00 PM"
 */
export const parseDateTime = (isoString) => {
  if (!isoString) return null;
  return moment(isoString).tz(SYSTEM_TIMEZONE);
};

/**
 * Форматировать дату для отображения (только дата)
 * 
 * @param {string} isoString - ISO datetime строка
 * @param {string} format - Формат вывода (default: 'MM/DD/YYYY')
 * @returns {string} Отформатированная дата
 * 
 * @example
 * formatDateOnly('2025-01-06T08:00:00.000Z') // "01/06/2025"
 */
export const formatDateOnly = (isoString, format = 'MM/DD/YYYY') => {
  if (!isoString) return '';
  return moment(isoString).tz(SYSTEM_TIMEZONE).format(format);
};

/**
 * Форматировать datetime для отображения
 * 
 * @param {string} isoString - ISO datetime строка
 * @param {string} format - Формат вывода (default: 'MM/DD/YYYY h:mm A')
 * @returns {string} Отформатированная дата и время
 * 
 * @example
 * formatDateTime('2025-01-06T22:00:00.000Z') // "01/06/2025 2:00 PM"
 */
export const formatDateTime = (isoString, format = 'MM/DD/YYYY h:mm A') => {
  if (!isoString) return '';
  return moment(isoString).tz(SYSTEM_TIMEZONE).format(format);
};

/**
 * Форматировать время для отображения
 * 
 * @param {string} isoString - ISO datetime строка
 * @param {string} format - Формат вывода (default: 'h:mm A')
 * @returns {string} Отформатированное время
 * 
 * @example
 * formatTimeOnly('2025-01-06T22:00:00.000Z') // "2:00 PM"
 */
export const formatTimeOnly = (isoString, format = 'h:mm A') => {
  if (!isoString) return '';
  return moment(isoString).tz(SYSTEM_TIMEZONE).format(format);
};

/**
 * Извлечь дату (YYYY-MM-DD) из ISO datetime
 * 
 * @param {string} isoString - ISO datetime строка
 * @returns {string} Дата в формате YYYY-MM-DD
 * 
 * @example
 * extractDate('2025-01-06T22:00:00.000Z') // "2025-01-06"
 */
export const extractDate = (isoString) => {
  if (!isoString) return null;
  return moment(isoString).tz(SYSTEM_TIMEZONE).format('YYYY-MM-DD');
};

/**
 * Извлечь время (HH:mm) из ISO datetime
 * 
 * @param {string} isoString - ISO datetime строка
 * @returns {string} Время в формате HH:mm (24h)
 * 
 * @example
 * extractTime('2025-01-06T22:00:00.000Z') // "14:00"
 */
export const extractTime = (isoString) => {
  if (!isoString) return null;
  return moment(isoString).tz(SYSTEM_TIMEZONE).format('HH:mm');
};

/**
 * Получить текущую дату в SYSTEM_TIMEZONE
 * 
 * @returns {string} Текущая дата в формате YYYY-MM-DD
 */
export const getTodayDate = () => {
  return moment().tz(SYSTEM_TIMEZONE).format('YYYY-MM-DD');
};

/**
 * Получить текущий datetime
 * 
 * @returns {string} Текущий datetime в ISO формате
 */
export const getNow = () => {
  return moment().utc().format();
};

/**
 * Проверить является ли дата сегодняшней
 * 
 * @param {string} isoString - ISO datetime строка
 * @returns {boolean}
 */
export const isToday = (isoString) => {
  if (!isoString) return false;
  return moment(isoString).tz(userTimezone).isSame(moment().tz(userTimezone), 'day');
};

/**
 * Проверить является ли дата прошедшей (по дням)
 * 
 * @param {string} isoString - ISO datetime строка
 * @returns {boolean}
 */
export const isPast = (isoString) => {
  if (!isoString) return false;
  return moment(isoString).tz(userTimezone).isBefore(moment().tz(userTimezone), 'day');
};

/**
 * Проверить является ли дата будущей
 * 
 * @param {string} isoString - ISO datetime строка
 * @returns {boolean}
 */
export const isFuture = (isoString) => {
  if (!isoString) return false;
  return moment(isoString).tz(userTimezone).isAfter(moment().tz(userTimezone));
};

/**
 * Получить начало дня для даты
 * 
 * @param {string} isoString - ISO datetime строка
 * @returns {string} ISO datetime начала дня
 */
export const startOfDay = (isoString) => {
  if (!isoString) return null;
  return moment(isoString).tz(SYSTEM_TIMEZONE).startOf('day').utc().format();
};

/**
 * Получить конец дня для даты
 * 
 * @param {string} isoString - ISO datetime строка
 * @returns {string} ISO datetime конца дня
 */
export const endOfDay = (isoString) => {
  if (!isoString) return null;
  return moment(isoString).tz(SYSTEM_TIMEZONE).endOf('day').utc().format();
};

// ============================================
// ADDITIONAL FORMATTING FUNCTIONS (from dateHelper)
// ============================================

/**
 * Форматировать дату из UTC в локальное время (короткий формат)
 * Alias для formatDateOnly с locale format
 * 
 * @param {string} date - ISO datetime строка
 * @param {string} format - Формат вывода (default: 'L' = locale date)
 * @returns {string} Отформатированная дата
 */
export const formatDate = (date, format = 'L') => {
  if (!date) return '';
  return moment.utc(date).tz(userTimezone).format(format);
};

/**
 * Форматировать только время
 * Alias для formatTimeOnly
 * 
 * @param {string} date - ISO datetime строка  
 * @param {string} format - Формат вывода (default: 'h:mm A')
 * @returns {string} Отформатированное время
 */
export const formatTime = (date, format = 'h:mm A') => {
  if (!date) return '';
  return moment.utc(date).tz(userTimezone).format(format);
};

/**
 * Конвертировать дату в UTC ISO string для отправки на API
 * 
 * @param {Date|string|moment.Moment} date - Дата для конвертации
 * @returns {string} ISO datetime string в UTC
 * 
 * @example
 * toUTC(new Date()) // "2025-01-06T22:00:00.000Z"
 */
export const toUTC = (date) => {
  if (!date) return '';
  return moment(date).utc().toISOString();
};

/**
 * Получить moment объект в timezone пользователя
 * 
 * @param {string} date - ISO datetime строка (опционально)
 * @returns {moment.Moment} Moment объект в userTimezone
 * 
 * @example
 * toLocal('2025-01-06T22:00:00.000Z').format('h:mm A') // "2:00 PM"
 * toLocal().format('YYYY-MM-DD') // текущая дата
 */
export const toLocal = (date) => {
  if (!date) return moment().tz(userTimezone);
  return moment.utc(date).tz(userTimezone);
};

/**
 * Получить текущий moment в timezone пользователя
 * 
 * @returns {moment.Moment} Текущий moment в userTimezone
 */
export const now = () => moment().tz(userTimezone);

/**
 * Форматировать серверное время (createdAt, updatedAt)
 * 
 * ПРОБЛЕМА: Сервер PostgreSQL использует "timestamp without timezone".
 * При JSON сериализации TypeORM добавляет "Z", но реально время уже в PST.
 * 
 * Решение: Берём "сырые" числа времени и показываем как PST.
 * 
 * @param {string|Date} date - Дата от сервера
 * @param {string} format - Формат вывода (default: 'L, h:mm:ss a')
 * @returns {string} Отформатированная дата
 */
export const formatServerDateTime = (date, format = 'L, h:mm:ss a') => {
  if (!date) return '';
  
  // Даты от сервера в UTC - конвертируем в SYSTEM_TIMEZONE
  return moment(date).tz(SYSTEM_TIMEZONE).format(format);
};

// ============================================
// EXPORTS
// ============================================

// Экспорт moment с настроенным timezone для использования напрямую
// ВАЖНО: Всегда импортируй moment из этого файла, а не из 'moment' напрямую!
export { moment };

// Default export для удобства
export default {
  // Constants
  SYSTEM_TIMEZONE,
  DEFAULT_TIMEZONE,
  
  // Timezone management
  setUserTimezone,
  getUserTimezone,
  getDefaultTimezone,
  
  // DatePicker
  getDateFromPicker,
  
  // Create/Parse
  createDateTime,
  createDateOnly,
  parseDateTime,
  toUTC,
  toLocal,
  
  // Format
  formatDateOnly,
  formatDateTime,
  formatTimeOnly,
  formatDate,
  formatTime,
  formatServerDateTime,
  
  // Extract
  extractDate,
  extractTime,
  
  // Current date/time
  getTodayDate,
  getNow,
  now,
  
  // Comparisons
  isToday,
  isPast,
  isFuture,
  
  // Day boundaries
  startOfDay,
  endOfDay,
  
  // Moment instance
  moment,
};

